import { Divider, Box, FormField, RadioGroup, Text, InfoIcon } from '@wix/design-system';
import React, { useContext, useMemo } from 'react';
import { RicosContext, ZIndexContext } from 'ricos-context';
import { PollData_Poll_Settings_Permissions_ViewRole, PollData_Poll_Settings_Permissions_VoteRole } from 'ricos-schema/next';
import { ToggleButton } from 'wix-rich-content-ui-components';
import { POLLS_MULTIPLE_ANSWERS_TOGGLE, POLLS_VOTE_COUNT_TOGGLE, POLLS_EACH_PERSON_VOTE_TOGGLE, POLL_VISIBILITY_OPTION_EVERYONE, POLL_VISIBILITY_OPTION_VOTERS, POLL_VISIBILITY_OPTION_CREATOR } from '../../dataHooks.js';
import { Section } from './section.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const PollSettingsSection = ({
  componentData,
  updateRicosDocument,
  config
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { t } = useContext(RicosContext);
  const updatePermissions = (permissions) => {
    var _a2, _b2, _c2;
    updateRicosDocument({
      poll: __spreadProps(__spreadValues({
        options: []
      }, componentData.poll), {
        settings: __spreadProps(__spreadValues({}, (_a2 = componentData.poll) == null ? void 0 : _a2.settings), {
          permissions: __spreadValues(__spreadValues({}, (_c2 = (_b2 = componentData.poll) == null ? void 0 : _b2.settings) == null ? void 0 : _c2.permissions), permissions)
        })
      })
    });
  };
  const updateSettings = (settings) => {
    var _a2;
    updateRicosDocument({
      poll: __spreadProps(__spreadValues({
        options: []
      }, componentData.poll), {
        settings: __spreadValues(__spreadValues({}, (_a2 = componentData == null ? void 0 : componentData.poll) == null ? void 0 : _a2.settings), settings)
      })
    });
  };
  const pollPermissions = (_b = (_a = componentData.poll) == null ? void 0 : _a.settings) == null ? void 0 : _b.permissions;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, config.showVoteRoleSetting && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Section, { title: t("Poll_PollSettings_Tab_Settings_Section_Voting_Header") }, /* @__PURE__ */ React.createElement(
    WhoCanVoteRadioGroup,
    {
      value: pollPermissions == null ? void 0 : pollPermissions.vote,
      onChange: (vote) => updatePermissions({ vote })
    }
  ), /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      dataHook: POLLS_MULTIPLE_ANSWERS_TOGGLE,
      title: t("Poll_PollSettings_Tab_Settings_Section_Voting_Multiselect"),
      checked: !!(pollPermissions == null ? void 0 : pollPermissions.allowMultipleVotes),
      onChange: () => updatePermissions({ allowMultipleVotes: !(pollPermissions == null ? void 0 : pollPermissions.allowMultipleVotes) })
    }
  )), /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Section, { title: t("Poll_PollSettings_Tab_Settings_Section_Results_Header") }, /* @__PURE__ */ React.createElement(
    WhoCanSeePollResultsRadioGroup,
    {
      value: pollPermissions == null ? void 0 : pollPermissions.view,
      onChange: (view) => updatePermissions({ view })
    }
  ), (pollPermissions == null ? void 0 : pollPermissions.view) !== PollData_Poll_Settings_Permissions_ViewRole.CREATOR && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      dataHook: POLLS_VOTE_COUNT_TOGGLE,
      title: t("Poll_PollSettings_Tab_Settings_Section_Results_VoteVisibility"),
      checked: !!((_d = (_c = componentData.poll) == null ? void 0 : _c.settings) == null ? void 0 : _d.showVotesCount),
      onChange: () => {
        var _a2, _b2, _c2, _d2;
        return updateSettings({
          showVoters: !((_b2 = (_a2 = componentData.poll) == null ? void 0 : _a2.settings) == null ? void 0 : _b2.showVotesCount),
          showVotesCount: !((_d2 = (_c2 = componentData.poll) == null ? void 0 : _c2.settings) == null ? void 0 : _d2.showVotesCount)
        });
      }
    }
  ), ((_f = (_e = componentData.poll) == null ? void 0 : _e.settings) == null ? void 0 : _f.showVotesCount) && /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      dataHook: POLLS_EACH_PERSON_VOTE_TOGGLE,
      title: t("Poll_PollSettings_Tab_Settings_Section_Results_VoterAnonymous"),
      checked: !!((_h = (_g = componentData.poll) == null ? void 0 : _g.settings) == null ? void 0 : _h.showVoters),
      onChange: () => {
        var _a2, _b2;
        return updateSettings({ showVoters: !((_b2 = (_a2 = componentData.poll) == null ? void 0 : _a2.settings) == null ? void 0 : _b2.showVoters) });
      }
    }
  ))));
};
const WhoCanVoteRadioGroup = ({ value, onChange }) => {
  const { t } = useContext(RicosContext);
  const voteRoleOptions = useMemo(
    () => [
      {
        value: PollData_Poll_Settings_Permissions_VoteRole.SITE_MEMBERS,
        label: t("Poll_PollSettings_Tab_Settings_Section_Voting_Permission_SiteMember")
      },
      {
        value: PollData_Poll_Settings_Permissions_VoteRole.ALL,
        label: t("Poll_PollSettings_Tab_Settings_Section_Voting_Permission_All")
      }
    ],
    [t]
  );
  return /* @__PURE__ */ React.createElement(
    RadioGroupWrapper,
    {
      label: t("Poll_PollSettings_Tab_Settings_Section_Voting_Permission_Title"),
      value,
      onChange,
      options: voteRoleOptions
    }
  );
};
const WhoCanSeePollResultsRadioGroup = ({ value, onChange }) => {
  const { t } = useContext(RicosContext);
  const viewRoleOptions = useMemo(
    () => [
      {
        dataHook: POLL_VISIBILITY_OPTION_EVERYONE,
        value: PollData_Poll_Settings_Permissions_ViewRole.EVERYONE,
        label: /* @__PURE__ */ React.createElement(
          TextWithInfo,
          {
            text: t("Poll_PollSettings_Tab_Settings_Section_Results_Permission_All"),
            infoContent: t("Poll_PollSettings_Tab_Settings_Section_Results_Permission_All_Tooltip")
          }
        )
      },
      {
        dataHook: POLL_VISIBILITY_OPTION_VOTERS,
        value: PollData_Poll_Settings_Permissions_ViewRole.VOTERS,
        label: /* @__PURE__ */ React.createElement(
          TextWithInfo,
          {
            text: t("Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters"),
            infoContent: t(
              "Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters_Tooltip"
            )
          }
        )
      },
      {
        dataHook: POLL_VISIBILITY_OPTION_CREATOR,
        value: PollData_Poll_Settings_Permissions_ViewRole.CREATOR,
        label: /* @__PURE__ */ React.createElement(
          TextWithInfo,
          {
            text: t("Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner"),
            infoContent: t(
              "Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner_Tooltip"
            )
          }
        )
      }
    ],
    [t]
  );
  return /* @__PURE__ */ React.createElement(
    RadioGroupWrapper,
    {
      label: t("Poll_PollSettings_Tab_Settings_Section_Results_Permission_Title"),
      value,
      onChange,
      options: viewRoleOptions
    }
  );
};
const RadioGroupWrapper = ({ label, value, onChange, options }) => {
  return /* @__PURE__ */ React.createElement(Box, { marginTop: "SP3", marginBottom: "SP2" }, /* @__PURE__ */ React.createElement(FormField, { label }, /* @__PURE__ */ React.createElement(RadioGroup, { value, onChange, size: "small" }, options.map(({ value: value2, label: label2, dataHook }) => /* @__PURE__ */ React.createElement(RadioGroup.Radio, { key: value2, value: value2, dataHook }, label2)))));
};
const TextWithInfo = ({ text, infoContent }) => {
  const zIndexService = useContext(ZIndexContext);
  return /* @__PURE__ */ React.createElement(Box, { verticalAlign: "middle", gap: "SP1" }, /* @__PURE__ */ React.createElement(Text, { size: "small" }, text), /* @__PURE__ */ React.createElement(
    InfoIcon,
    {
      size: "small",
      content: infoContent,
      tooltipProps: {
        appendTo: (el) => {
          var _a;
          return Boolean((_a = el.getAttribute("data-hook")) == null ? void 0 : _a.includes("ricos-portal"));
        },
        zIndex: zIndexService.getZIndex("TOOLTIP")
      }
    }
  ));
};

export { PollSettingsSection };
//# sourceMappingURL=poll-settings-section.js.map
